import React, { useState, useEffect } from 'react';
import axios from 'axios';

/* ---------------------------------------------------------------------------
   Dashboard overview
   Flat, professional dark surfaces with a single red accent — built on the
   shared design tokens (.card, .eyebrow, .badge-*, .table-head, …).
--------------------------------------------------------------------------- */

const StatCard = ({ label, value, sub, icon }) => (
  <div className="card px-4 py-3.5 flex items-start justify-between">
    <div>
      <p className="eyebrow mb-2">{label}</p>
      <p className="text-xl font-bold text-white tabular-nums">{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
    </div>
    {icon && (
      <div className="w-10 h-10 rounded-lg bg-surface-600 border border-line-strong flex items-center justify-center text-accent-soft">
        {icon}
      </div>
    )}
  </div>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const WalletIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
      d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3"
    />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
  </svg>
);

function Dashboard() {
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    accountBalance: 0,
    accountCurrency: 'ZMW',
    subscriptionPlans: [],
    recentSubscriptions: [],
    userDetails: {},
    loading: true,
  });

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('token');
        const [usersResponse, subscriptionsResponse, plansResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/subscriptions/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/subscription-plans`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const activeSubscriptions = subscriptionsResponse.data.filter(
          (sub) => sub.startDate && sub.endDate && new Date(sub.endDate) > new Date()
        );

        const totalRevenue = subscriptionsResponse.data
          .filter((sub) => sub.startDate && sub.endDate)
          .reduce((sum, sub) => {
            const price = parseFloat(sub.plan?.price?.replace(/[^0-9.]/g, '') || 0);
            return sum + price;
          }, 0);

        const recentSubs = activeSubscriptions.slice(0, 6);
        const userDetails = {};

        let accountBalance = 0;
        let accountCurrency = 'ZMW';

        try {
          const accountsResponse = await axios.get(
            'https://payment-lenco-api.onrender.com/api/payment/accounts'
          );
          const primaryAccount = accountsResponse?.data?.data?.[0];
          accountBalance = parseFloat(primaryAccount?.availableBalance || 0);
          accountCurrency = primaryAccount?.currency || accountCurrency;
        } catch (balanceError) {
          console.error('Error fetching account balance:', balanceError);
        }

        for (const sub of recentSubs) {
          if (!userDetails[sub.userId]) {
            try {
              const userResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/users/${sub.userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              userDetails[sub.userId] = userResponse.data;
            } catch (error) {
              console.error(`Error fetching user details for userId ${sub.userId}:`, error);
              userDetails[sub.userId] = { name: 'Unknown User' };
            }
          }
        }

        setAnalytics({
          totalUsers: usersResponse.data.length,
          activeSubscriptions: activeSubscriptions.length,
          totalRevenue,
          accountBalance,
          accountCurrency,
          subscriptionPlans: plansResponse.data,
          recentSubscriptions: recentSubs,
          userDetails,
          loading: false,
        });
      } catch (error) {
        console.error('Error fetching analytics:', error);
        setAnalytics((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchAnalytics();
  }, []);

  const fmtMoney = (n) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const allPlans = analytics.subscriptionPlans || [];

  return (
    <div className="space-y-4">
      {/* Page heading */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow-accent mb-1">Overview</p>
          <h1 className="section-title">Dashboard</h1>
          <p className="muted text-sm mt-1">
            Key numbers across users, subscriptions and revenue.
          </p>
        </div>
        <p className="text-xs text-gray-600">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <StatCard
          label="Total users"
          value={analytics.totalUsers.toLocaleString()}
          icon={<UsersIcon />}
          sub="Registered accounts"
        />
        <StatCard
          label="Active subscriptions"
          value={analytics.activeSubscriptions.toLocaleString()}
          icon={<CheckIcon />}
          sub="Not expired"
        />
        <StatCard
          label="Revenue (all time)"
          value={fmtMoney(analytics.totalRevenue)}
          sub="Sum of plan prices"
          icon={<ChartIcon />}
        />
        <StatCard
          label="Wallet balance"
          value={fmtMoney(analytics.accountBalance)}
          sub={analytics.accountCurrency}
          icon={<WalletIcon />}
        />
      </div>

      {/* Subscription plans */}
      <div className="card">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div>
            <h2 className="text-base font-semibold text-white">Subscription plans</h2>
            <p className="text-xs text-gray-500 mt-0.5">Pricing tiers offered to subscribers</p>
          </div>
          <span className="badge-neutral">{allPlans.length} plans</span>
        </div>
        {allPlans.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-500 text-center">No plans configured yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {allPlans.map((plan) => (
              <div
                key={plan.planId}
                className="bg-surface-900 border border-line rounded-lg p-4 hover:border-line-strong transition-colors"
              >
                <div className="flex items-start justify-between">
                  <h3 className="text-sm font-semibold text-white">{plan.planName}</h3>
                  <span className="badge-neutral">{plan.duration}</span>
                </div>
                <p className="mt-3 text-xl font-bold text-accent-soft tabular-nums">
                  {plan.price}
                </p>
                {plan.maxVotes != null && (
                  <p className="mt-1 text-xs text-gray-500">
                    {plan.maxVotes.toLocaleString()} votes included
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent subscriptions */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div>
            <h2 className="text-base font-semibold text-white">Recent subscriptions</h2>
            <p className="text-xs text-gray-500 mt-0.5">Latest active subscribers</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="table-head">
                <th className="px-4 py-2.5">User</th>
                <th className="px-4 py-2.5">Plan</th>
                <th className="px-4 py-2.5">Start</th>
                <th className="px-4 py-2.5">End</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {analytics.recentSubscriptions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No recent subscriptions.
                  </td>
                </tr>
              ) : (
                analytics.recentSubscriptions.map((sub) => (
                  <tr key={sub.subscriptionId} className="table-row">
                    <td className="px-4 py-2.5 text-white font-medium">
                      {analytics.userDetails[sub.userId]?.name || 'Unknown User'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-300">{sub.plan?.planName || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-400">{fmtDate(sub.startDate)}</td>
                    <td className="px-4 py-2.5 text-gray-400">{fmtDate(sub.endDate)}</td>
                    <td className="px-4 py-2.5">
                      <span className={sub.active ? 'badge-success' : 'badge-neutral'}>
                        {sub.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
